import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Link,
  IconButton,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AdminAuthModal from '../Admin/AdminAuthModal';
import {
  Facebook as FacebookIcon,
  Twitter as TwitterIcon,
  Instagram as InstagramIcon,
  YouTube as YouTubeIcon,
  LinkedIn as LinkedInIcon,
  GitHub as GitHubIcon,
  Link as LinkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
} from '@mui/icons-material';
import { useTranslation } from '../../contexts/TranslationContext';

import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';

type FooterLink = {
  label: string;
  href: string;
  newTab?: boolean;
};

type FooterColumn = {
  title: string;
  links: FooterLink[];
};

type FooterSocial = {
  platform: 'facebook' | 'twitter' | 'instagram' | 'youtube' | 'tiktok' | 'linkedin' | 'github' | 'custom';
  href: string;
  label?: string;
};

type FooterCompany = {
  blurb?: string;
  email?: string;
  phone?: string;
  address?: string;
  logoPath?: string;
  wordmarkPath?: string;
};

export type FooterConfig = {
  enabled?: boolean;
  showOnHomepage?: boolean;
  company?: FooterCompany;
  columns?: FooterColumn[];
  social?: FooterSocial[];
  legalLinks?: FooterLink[];
  copyrightText?: string;
};

type Props = {
  configOverride?: FooterConfig;
  forceShow?: boolean;
};

const Footer: React.FC<Props> = ({ configOverride, forceShow }) => {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [showAdminLink, setShowAdminLink] = useState<boolean>(false);
  const { t } = useTranslation();
  const { settings } = useWebsiteSettings();

  React.useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch('/api/admin-availability.php', { method: 'GET' });
        const json = await res.json().catch(() => null);
        if (!cancelled) {
          setShowAdminLink(Boolean(json?.ok && json?.showAdminLink));
        }
      } catch {
        if (!cancelled) {
          setShowAdminLink(false);
        }
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const defaultColumns: FooterColumn[] = [
    {
      title: t('footer.sections.shop.title'),
      links: [
        { label: t('footer.sections.shop.women'), href: '/products?category=women' },
        { label: t('footer.sections.shop.men'), href: '/products?category=men' },
        { label: t('footer.sections.shop.kids'), href: '/products?category=kids' },
        { label: t('footer.sections.shop.accessories'), href: '/products?category=accessories' },
        { label: t('footer.sections.shop.sale'), href: '/products?sale=true' },
      ],
    },
    {
      title: t('footer.sections.service.title'),
      links: [
        { label: t('footer.sections.service.contact'), href: '/contact' },
        { label: t('footer.sections.service.sizeGuide'), href: '/size-guide' },
        { label: t('footer.sections.service.shipping'), href: '/shipping' },
        { label: t('footer.sections.service.returns'), href: '/returns' },
        { label: t('footer.sections.service.faq'), href: '/faq' },
      ],
    },
    {
      title: t('footer.sections.company.title'),
      links: [
        { label: t('footer.sections.company.about'), href: '/about' },
        { label: t('footer.sections.company.careers'), href: '/careers' },
        { label: t('footer.sections.company.press'), href: '/press' },
        { label: t('footer.sections.company.sustainability'), href: '/sustainability' },
        { label: t('footer.sections.company.investors'), href: '/investors' },
      ],
    },
    {
      title: t('footer.sections.sell.title'),
      links: [
        { label: t('footer.sections.sell.start'), href: '/register?role=seller' },
        { label: t('footer.sections.sell.center'), href: '/seller' },
        { label: t('footer.sections.sell.guidelines'), href: '/seller-guidelines' },
        { label: t('footer.sections.sell.fees'), href: '/seller-fees' },
        { label: t('footer.sections.sell.support'), href: '/seller-support' },
      ],
    },
  ];

  const defaultSocial: FooterSocial[] = [
    { platform: 'facebook', href: 'https://facebook.com', label: t('footer.social.facebook') },
    { platform: 'twitter', href: 'https://twitter.com', label: t('footer.social.twitter') },
    { platform: 'instagram', href: 'https://instagram.com', label: t('footer.social.instagram') },
    { platform: 'youtube', href: 'https://youtube.com', label: t('footer.social.youtube') },
  ];

  const defaultLegal: FooterLink[] = [
    { label: t('footer.legal.privacy'), href: '/datenschutz' },
    { label: t('footer.legal.terms'), href: '/agb' },
    { label: t('footer.legal.cookies'), href: '/cookies' },
    { label: t('footer.legal.accessibility'), href: '/accessibility' },
    { label: t('footer.legal.rules'), href: '/regeln' },
  ];

  const cfg: FooterConfig = React.useMemo(() => {
    const fromSettings = (settings as any)?.footer;
    const base = (configOverride && typeof configOverride === 'object')
      ? configOverride
      : (fromSettings && typeof fromSettings === 'object' ? (fromSettings as FooterConfig) : ({} as FooterConfig));
    return base;
  }, [configOverride, settings]);

  const enabled = cfg?.enabled !== false;
  if (!enabled && !forceShow) return null;

  const company = (cfg?.company && typeof cfg.company === 'object') ? cfg.company : {};
  const columns = (Array.isArray(cfg?.columns) && cfg.columns.length > 0) ? cfg.columns : defaultColumns;
  const social = (Array.isArray(cfg?.social) && cfg.social.length > 0) ? cfg.social : defaultSocial;
  const legalLinks = (Array.isArray(cfg?.legalLinks) && cfg.legalLinks.length > 0) ? cfg.legalLinks : defaultLegal;
  const copyrightText = typeof cfg?.copyrightText === 'string' && cfg.copyrightText.trim() !== ''
    ? cfg.copyrightText
    : `© ${currentYear} Sistasouk. ${t('footer.bottom.allRightsReserved')}`;

  const iconForSocial = (p: FooterSocial['platform']) => {
    if (p === 'facebook') return <FacebookIcon />;
    if (p === 'twitter') return <TwitterIcon />;
    if (p === 'instagram') return <InstagramIcon />;
    if (p === 'youtube') return <YouTubeIcon />;
    if (p === 'linkedin') return <LinkedInIcon />;
    if (p === 'github') return <GitHubIcon />;
    return <LinkIcon />;
  };

  return (
    <Box
      component="footer"
      sx={{
        bgcolor: 'grey.900',
        color: 'white',
        mt: 'auto',
      }}
    >
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {/* Company Info */}
          <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <img src={company?.logoPath || '/sistasouk.png'} alt="Logo" style={{ height: 32 }} />
              <img src={company?.wordmarkPath || '/sistasouk_font.png'} alt="Wordmark" style={{ height: 28 }} />
            </Box>
            <Typography variant="body2" color="grey.300" paragraph>
              {company?.blurb || t('footer.company.blurb')}
            </Typography>
            
            {/* Contact Info */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EmailIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2" color="grey.300">
                  {company?.email || 'support@sistasouk.com'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <PhoneIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2" color="grey.300">
                  {company?.phone || '+1 (555) 123-4567'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2" color="grey.300">
                  {company?.address || '123 Fashion Street, New York, NY 10001'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Footer Links */}
          {columns.map((section, index) => (
            <Box key={index} sx={{ flex: '1 1 200px', minWidth: '200px' }}>
              <Typography variant="h6" fontWeight="600" gutterBottom>
                {section.title}
              </Typography>
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                {section.links.map((link, linkIndex) => (
                  <Box component="li" key={linkIndex} sx={{ mb: 1 }}>
                    <Link
                      href={link.href}
                      color="grey.300"
                      underline="hover"
                      target={link.newTab ? '_blank' : undefined}
                      rel={link.newTab ? 'noopener noreferrer' : undefined}
                      sx={{
                        textDecoration: 'none',
                        '&:hover': {
                          color: 'primary.main',
                        },
                      }}
                    >
                      {link.label}
                    </Link>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>

        <Divider sx={{ my: 4, bgcolor: 'grey.700' }} />

        {/* Bottom Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Typography 
            variant="body2" 
            color="grey.400"
          >
            {copyrightText}
          </Typography>

          {/* Social Links */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            {social.map((s, index) => (
              <IconButton
                key={index}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: 'grey.300',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: 'rgba(14, 165, 233, 0.1)',
                  },
                }}
                aria-label={s.label || s.platform}
              >
                {iconForSocial(s.platform)}
              </IconButton>
            ))}
          </Box>
        </Box>

        {/* Legal Links */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 3,
            mt: 2,
            justifyContent: 'center',
          }}
        >
          {legalLinks.map((l, idx) => (
            <Link
              key={idx}
              href={l.href}
              color="grey.400"
              underline="hover"
              variant="body2"
              target={l.newTab ? '_blank' : undefined}
              rel={l.newTab ? 'noopener noreferrer' : undefined}
            >
              {l.label}
            </Link>
          ))}
          {showAdminLink ? (
            <Link
              href="#"
              color="primary.main"
              underline="hover"
              variant="body2"
              onClick={(e) => {
                e.preventDefault();
                setAdminModalOpen(true);
              }}
              sx={{
                fontWeight: 'bold',
                '&:hover': {
                  color: 'primary.light',
                }
              }}
            >
              {t('footer.admin.signIn')}
            </Link>
          ) : null}
        </Box>
      </Container>

      {/* Admin Auth Modal */}
      <AdminAuthModal
        open={adminModalOpen}
        onClose={() => setAdminModalOpen(false)}
        onSuccess={() => {
          setAdminModalOpen(false);
          navigate('/admin');
        }}
      />
    </Box>
  );
};

export default Footer;
